/**
 * GenericResultsSection component.
 *
 * This component provides a reusable template for rendering different types
 * of results sections (post-processing, quantification, PIPS results, etc.).
 * It reduces code duplication by providing a common pattern for displaying
 * orchestrations organized by stain type.
 */

import { Grid, Typography } from '@mui/material';
import { keyBy, map, size } from 'lodash';
import React from 'react';

import { ClassificationBinningParams, SlideInferenceResults } from 'interfaces/calculateFeatures';
import { ModelPreferencesById, ModelUserPreferences } from 'utils/models';
import { OrchestrationBySlideByResultName, OrchestrationByStainByResultName } from 'utils/orchestrationSelections';
import InferenceStain from './InferenceModelsSection/InferenceStain';

export interface GenericResultsSectionProps {
  studyId: string;
  title?: string;
  artifactsByStain: OrchestrationByStainByResultName;
  slides: SlideInferenceResults[];
  selectedOrchestrations?: OrchestrationBySlideByResultName;
  modelPreferencesByModelId?: ModelPreferencesById;
  setSelectedOrchestrations: (slideIds: string[], resultKey?: string, orchestration?: any) => void;
  setModelPreferencesByModelId: (modelId: string, userPreferences: ModelUserPreferences) => void;
  setSelectedOrchestrationsByClassificationType: (modelType: string, previousModelType?: string) => void;
  setIntensityClassificationBinning: (modelId: string, binning: ClassificationBinningParams) => void;
  enableSpotDetectionSetup?: boolean;
  availableCellSegmentationGeometries?: string[];
  disableIfNoJob?: boolean;
}

/**
 * Generic component for rendering results sections.
 *
 * This component provides a consistent layout and behavior for different
 * types of results sections. It handles the common pattern of displaying
 * orchestrations grouped by stain type with expandable sections.
 */
const GenericResultsSection: React.FC<GenericResultsSectionProps> = ({
  studyId,
  title,
  artifactsByStain,
  slides,
  selectedOrchestrations,
  modelPreferencesByModelId,
  setSelectedOrchestrations,
  setModelPreferencesByModelId,
  setSelectedOrchestrationsByClassificationType,
  enableSpotDetectionSetup = false,
  availableCellSegmentationGeometries,
  setIntensityClassificationBinning,
  disableIfNoJob = false,
}) => {
  const slidesById = keyBy(slides, 'slideId');

  return (
    <Grid item container spacing={1}>
      {title && (
        <Grid item>
          <Typography variant="subtitle1" fontWeight={700}>
            {title}:
          </Typography>
        </Grid>
      )}
      <Grid item xs={12}>
        <Grid container spacing={1} direction="column">
          {map(artifactsByStain, (orchestrationByResultName, stain) => {
            return (
              <Grid item key={stain}>
                <InferenceStain
                  studyId={studyId}
                  key={stain}
                  stain={stain}
                  orchestrationByResultName={orchestrationByResultName}
                  slides={slidesById}
                  selectedOrchestrationsByResultName={selectedOrchestrations}
                  modelPreferencesByModelId={modelPreferencesByModelId}
                  setSelectedOrchestrationsByResultName={setSelectedOrchestrations}
                  setIntensityClassificationBinning={setIntensityClassificationBinning}
                  setModelPreferencesByModelId={setModelPreferencesByModelId}
                  availableCellSegmentationGeometries={availableCellSegmentationGeometries}
                  enableSpotDetectionSetup={enableSpotDetectionSetup}
                  setSelectedOrchestrationsByClassificationType={setSelectedOrchestrationsByClassificationType}
                  defaultExpanded={enableSpotDetectionSetup || size(artifactsByStain) === 1}
                  disableIfNoJob={disableIfNoJob}
                />
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default GenericResultsSection;
